/**
 * Format date as YYYY-MM-DD without timezone conversion
 * This ensures consistent dates across all devices regardless of timezone
 */
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get date range based on period selection
 * Uses local date formatting to avoid timezone issues across devices
 */
export const getDateRange = (period, customFromDate, customToDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  switch (period) {
    case "This Week": {
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      return {
        startDate: formatDateLocal(startDate),
        endDate: formatDateLocal(today),
      };
    }
    case "This Month": {
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: formatDateLocal(startDate),
        endDate: formatDateLocal(today),
      };
    }
    case "Last Month": {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        startDate: formatDateLocal(lastMonth),
        endDate: formatDateLocal(lastDayOfLastMonth),
      };
    }
    case "Custom":
      if (customFromDate && customToDate && customFromDate.trim() && customToDate.trim()) {
        return { startDate: customFromDate.trim(), endDate: customToDate.trim() };
      }
      return null;
    default:
      return null;
  }
};

/**
 * Process NAV series data for chart
 */
export const processNavSeriesData = (historicalSignalSet, benchmark, dateRange) => {
  if (!historicalSignalSet?.navs?.strategyNavSeries) return [];

  const navs = historicalSignalSet.navs;
  const strategySeries = navs.strategyNavSeries;
  const benchmarkSeries = benchmark ? navs[`${benchmark.toLowerCase()}NavSeries`] : null;
  const longSeries = navs.longNavSeries || null;
  const shortSeries = navs.shortNavSeries || null;

  // Get all dates from strategy series
  const dates = Object.keys(strategySeries).sort((a, b) => a.localeCompare(b));

  // Filter by date range
  const filteredDates = dateRange?.startDate && dateRange?.endDate
    ? dates.filter((d) => {
        const dateStr = d.split('T')[0];
        const startDate = dateRange.startDate.split('T')[0];
        const endDate = dateRange.endDate.split('T')[0];
        return dateStr >= startDate && dateStr <= endDate;
      })
    : dates;

  // Build chart data - Strategy is always included
  const chartData = filteredDates.map((date) => {
    const dataPoint = { date, Strategy: strategySeries[date] };
    if (benchmarkSeries && benchmarkSeries[date] !== undefined) {
      dataPoint[benchmark] = benchmarkSeries[date];
    }
    if (longSeries && longSeries[date] !== undefined) {
      dataPoint.Long = longSeries[date];
    }
    if (shortSeries && shortSeries[date] !== undefined) {
      dataPoint.Short = shortSeries[date];
    }
    return dataPoint;
  });

  // Rebase data if dateRange is provided (divide all values by the first value)
  if (!dateRange?.startDate || !dateRange?.endDate || chartData.length === 0) {
    return chartData;
  }

  const firstDataPoint = chartData[0];
  const strategyFirstValue = firstDataPoint.Strategy;
  const benchmarkFirstValue = firstDataPoint[benchmark];
  const longFirstValue = firstDataPoint.Long;
  const shortFirstValue = firstDataPoint.Short;

  const rebaseValue = (value, firstValue) => {
    if (firstValue != null && typeof firstValue === 'number' && firstValue !== 0 &&
        value != null && typeof value === 'number') {
      return value / firstValue;
    }
    return value;
  };

  return chartData.map((dataPoint) => {
    const rebasedPoint = {
      date: dataPoint.date,
      Strategy: rebaseValue(dataPoint.Strategy, strategyFirstValue),
    };
    
    if (benchmark && dataPoint[benchmark] !== undefined) {
      rebasedPoint[benchmark] = rebaseValue(dataPoint[benchmark], benchmarkFirstValue);
    }
    if (dataPoint.Long !== undefined) {
      rebasedPoint.Long = rebaseValue(dataPoint.Long, longFirstValue);
    }
    if (dataPoint.Short !== undefined) {
      rebasedPoint.Short = rebaseValue(dataPoint.Short, shortFirstValue);
    }
    
    return rebasedPoint;
  });
};

/**
 * Calculate Y-axis domain for chart
 */
export const calculateYAxisDomain = (chartData, yScale, logMin) => {
  if (!chartData.length) return yScale === "log" ? [0.1, 10] : [0.7, 4.0];
  
  // Filter out date strings and get only numeric values
  const allVals = chartData.flatMap((d) =>
    Object.entries(d)
      .filter(([key]) => key !== "date")
      .map(([, value]) => value)
      .filter((v) => typeof v === "number" && v !== null && isFinite(v))
  );
  
  if (!allVals.length) return yScale === "log" ? [0.1, 10] : [0.7, 4.0];
  
  if (yScale === "log") {
    // For log scale, filter out non-positive values and ensure domain is valid
    const positiveVals = allVals.filter((v) => v > 0);
    if (!positiveVals.length) return [0.1, 10];
    const max = Math.max(...positiveVals);
    // Return domain with calculated logMin and a reasonable max (10% padding)
    return [logMin, max * 1.1];
  }
  
  // Linear scale
  const min = Math.min(...allVals);
  const max = Math.max(...allVals);
  const padding = (max - min) * 0.1;
  return [Math.max(0, min - padding), max + padding];
};

/**
 * Calculate log scale minimum
 */
export const calculateLogMin = (chartData) => {
  // Filter out date strings and get only positive numeric values
  const vals = chartData.flatMap((d) =>
    Object.entries(d)
      .filter(([key]) => key !== "date")
      .map(([, value]) => value)
      .filter((v) => typeof v === "number" && v > 0 && v !== null && isFinite(v))
  );
  if (!vals.length) return 0.1;
  const min = Math.min(...vals);
  // Ensure minimum is at least 0.1 for log scale to work properly, and use 90% of min for padding
  return Math.max(0.1, min * 0.9);
};

